import type { VoltacallTransferSignatureRequestPayload } from "@/domains/voltacall/voltacall.types";
import type { SiteId } from "@/types/common/ids";
import type { MaybeRef } from "vue";

import { useMutation, useQueryClient } from "@tanstack/vue-query";
import { toValue } from "vue";

import { TransferSignatureQueryKeys } from "@/api/backoffice-v2/voltacall/transfer-signature-query-keys.ts";
import { useBackofficeClient } from "@/hooks/backoffice/use-backoffice-client";
import { brandNumber } from "@/lib/branded";

export function useTransferSignatureMutations(siteId: MaybeRef<SiteId>) {
  const backofficeClient = useBackofficeClient();
  const queryClient = useQueryClient();

  const { mutateAsync: createTransferSignature, isPending: isCreatingTransferSignature } = useMutation({
    mutationKey: TransferSignatureQueryKeys.transferSignature(toValue(siteId) ?? brandNumber(0)),
    mutationFn: async (payload: VoltacallTransferSignatureRequestPayload) =>
      backofficeClient.transferSignature.createTransferSignature(toValue(siteId) ?? brandNumber(0), payload),
    onSuccess: () => {
      const resolvedSiteId = toValue(siteId);
      return Promise.all([
        queryClient.invalidateQueries({
          queryKey: TransferSignatureQueryKeys.transferSignature(resolvedSiteId),
        }),
        queryClient.invalidateQueries({
          queryKey: TransferSignatureQueryKeys.subscriberDetails(resolvedSiteId),
        }),
      ]);
    },
  });

  const { mutateAsync: cancelTransferSignature, isPending: isCancelingTransferSignature } = useMutation({
    mutationKey: TransferSignatureQueryKeys.transferSignature(toValue(siteId) ?? brandNumber(0)),
    mutationFn: async () =>
      backofficeClient.transferSignature.cancelTransferSignature(toValue(siteId) ?? brandNumber(0)),
    onSuccess: () => {
      const resolvedSiteId = toValue(siteId);
      return Promise.all([
        queryClient.invalidateQueries({
          queryKey: TransferSignatureQueryKeys.transferSignature(resolvedSiteId),
        }),
        queryClient.invalidateQueries({
          queryKey: TransferSignatureQueryKeys.subscriberDetails(resolvedSiteId),
        }),
      ]);
    },
  });

  return {
    createTransferSignature,
    isCreatingTransferSignature,
    cancelTransferSignature,
    isCancelingTransferSignature,
  };
}
